import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift } from 'lucide-react';
import { trackEasterEgg } from '../utils/analytics';

const EasterEggManager = () => {
  const [activeEgg, setActiveEgg] = useState(null);
  const [idleTime, setIdleTime] = useState(0);
  const [keySequence, setKeySequence] = useState('');
  const [discoveredEggs, setDiscoveredEggs] = useState(() => {
    const stored = localStorage.getItem('discoveredEggs');
    return stored ? JSON.parse(stored) : [];
  });

  // Easter Egg 1: The Patient Builder - 25 seconds idle
  useEffect(() => {
    let idleTimer;
    
    const resetIdleTimer = () => {
      setIdleTime(0);
    };

    const incrementIdle = () => {
      setIdleTime((prev) => {
        if (prev >= 25 && !discoveredEggs.includes('LION25')) {
          setActiveEgg({
            name: 'The Patient Builder',
            code: 'LION25',
            message: 'Patience builds empires. Here\'s yours:',
            description: '25% off on your first bulk inquiry'
          });
          trackEasterEgg('patient_builder', 'LION25');
          return 0;
        }
        return prev + 1;
      });
    };

    // Only track on homepage
    if (window.location.pathname === '/') {
      idleTimer = setInterval(incrementIdle, 1000);
      
      window.addEventListener('mousemove', resetIdleTimer);
      window.addEventListener('scroll', resetIdleTimer);
      window.addEventListener('keydown', resetIdleTimer);
      window.addEventListener('click', resetIdleTimer);
    }

    return () => {
      if (idleTimer) clearInterval(idleTimer);
      window.removeEventListener('mousemove', resetIdleTimer);
      window.removeEventListener('scroll', resetIdleTimer);
      window.removeEventListener('keydown', resetIdleTimer);
      window.removeEventListener('click', resetIdleTimer);
    };
  }, [discoveredEggs]);

  // Easter Egg 3: The Konami Builder - A-V-I sequence
  const handleKeyPress = useCallback((e) => {
    const key = e.key.toUpperCase();
    if ('AVI'.includes(key)) {
      setKeySequence((prev) => {
        const newSequence = (prev + key).slice(-3);
        if (newSequence === 'AVI' && !discoveredEggs.includes('AVI2025')) {
          setActiveEgg({
            name: 'The Konami Builder',
            code: 'AVI2025',
            message: 'You know the name. We know the craft.',
            description: 'Free sample kit with distributor enquiry'
          });
          trackEasterEgg('konami_builder', 'AVI2025');
          return '';
        }
        return newSequence;
      });
    } else {
      setKeySequence('');
    }
  }, [discoveredEggs]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Easter Egg 2: The Explorer - All products clicked (handled in ProductsPage)
  useEffect(() => {
    const handleExplorerEgg = (e) => {
      if (e.detail && e.detail.code === 'BUILDER12' && !discoveredEggs.includes('BUILDER12')) {
        setActiveEgg({
          name: 'The Explorer',
          code: 'BUILDER12',
          message: "You've seen everything we build. Now build something great.",
          description: 'Special pricing on your first order'
        });
        trackEasterEgg('explorer', 'BUILDER12');
      }
    };

    window.addEventListener('explorerEggTriggered', handleExplorerEgg);
    return () => window.removeEventListener('explorerEggTriggered', handleExplorerEgg);
  }, [discoveredEggs]);

  const closeEgg = () => {
    if (activeEgg) {
      const newDiscovered = [...discoveredEggs, activeEgg.code];
      setDiscoveredEggs(newDiscovered);
      localStorage.setItem('discoveredEggs', JSON.stringify(newDiscovered));
    }
    setActiveEgg(null);
  };

  const copyCode = () => {
    if (activeEgg) {
      navigator.clipboard.writeText(activeEgg.code);
    }
  };

  return (
    <AnimatePresence>
      {activeEgg && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-charcoal/95"
          data-testid="easter-egg-modal"
        >
          {/* Animated brick building effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-16 h-8 bg-clay/20"
                initial={{ 
                  y: '100vh',
                  x: Math.random() * window.innerWidth,
                }}
                animate={{ 
                  y: Math.random() * window.innerHeight,
                }}
                transition={{ 
                  duration: 1 + Math.random(),
                  delay: i * 0.05,
                  ease: 'easeOut'
                }}
              />
            ))}
          </div>

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className="relative bg-forest p-8 md:p-12 max-w-lg mx-4 text-center"
          >
            <button
              onClick={closeEgg}
              className="absolute top-4 right-4 text-limestone/60 hover:text-limestone transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring' }}
              className="w-20 h-20 bg-clay/20 mx-auto mb-6 flex items-center justify-center"
            >
              <Gift className="w-10 h-10 text-clay" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="font-oswald text-2xl md:text-3xl text-limestone uppercase tracking-wider mb-2"
            >
              {activeEgg.name}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-limestone/80 font-inter mb-6"
            >
              {activeEgg.message}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-charcoal p-4 mb-4"
            >
              <span className="font-mono text-3xl text-clay tracking-widest">
                {activeEgg.code}
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-limestone/60 text-sm font-inter mb-6"
            >
              {activeEgg.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <button
                onClick={copyCode}
                className="bg-clay text-white font-oswald uppercase tracking-wider px-6 py-3 hover:bg-clay/90 transition-colors"
              >
                Copy Code
              </button>
              <button
                onClick={closeEgg}
                className="border-2 border-limestone/30 text-limestone font-oswald uppercase tracking-wider px-6 py-3 hover:bg-limestone/10 transition-colors"
              >
                Continue Browsing
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EasterEggManager;
